import { forwardRef, useImperativeHandle } from "react";
import {
  Box,
  Button,
  DetailsDisclosure,
  Grid,
  Heading,
  Image,
  Label,
  Paragraph,
  Section,
  Spacer,
} from "@alto-avios/alto-ui";
import { useBreakpoint } from "@hooks/useBreakpoint";

import type { BalanceBoosterMultiplierHandle } from "../../BalanceBoosterMultiplier.types";
import { BoostMultiplierCard } from "../../BoostMultiplierCard";
import type { UseBoosterCoreProps } from "../../core/useBoosterCore";
import { useBoosterCore } from "../../core/useBoosterCore";

import { RewardPlatformBoosterUISkeleton } from "./RewardPlatformBoosterUI.skeleton";

/** Reward Platform variant - uses core hook with Reward Platform-specific UI */
export const RewardPlatformBoosterUI = forwardRef<BalanceBoosterMultiplierHandle, UseBoosterCoreProps>(
  (
    { aviosAmount, defaultSelectedBoostId, disabled, onBoostSelect, onBoostRemove, onError, onEligibilityChange },
    ref,
  ) => {
    const breakpoint = useBreakpoint();
    const booster = useBoosterCore({
      aviosAmount,
      defaultSelectedBoostId,
      disabled,
      onBoostSelect,
      onBoostRemove,
      onError,
      onEligibilityChange,
    });

    useImperativeHandle(ref, () => ({ reset: booster.handleResetBooster }), [booster.handleResetBooster]);

    // Hide entirely once confirmed not eligible/disabled.
    if (!booster.content || (!booster.isLoading && booster.disabled)) {
      return null;
    }

    // Any description hides the best-value badge on all cards.
    const hasAnyDescription = booster.multiplierOptions.some((option) => option.description);

    return (
      <Section paddingX={{ base: "sm", md: "lg" }} paddingY={{ base: "lg", md: "3xl" }} backgroundColor="layer1">
        <Box
          flexDirection="column"
          gap="2xl"
          backgroundColor="layer2"
          borderRadius="xs"
          borderWidth="sm"
          padding={{ base: "sm", xl: "2xl" }}
          borderColour="tertiary"
          width="930px"
          minHeight="494px"
        >
          {booster.isLoading ? (
            <RewardPlatformBoosterUISkeleton />
          ) : (
            <>
              <Box flexDirection={{ base: "column", xl: "row" }} gap={{ base: "2xl", xl: "xs" }}>
                <Grid columns={{ xl: 2 }}>
                  <Box flexDirection="column" gap="2xs">
                    <Image
                      src={booster.displayBoosterIcon}
                      alt={booster.content?.aviosBoostIcon?.alt || ""}
                      aria-hidden="true"
                      width="48px"
                      height="48px"
                      fit="contain"
                    />
                    <Box flexDirection="column" gap="sm">
                      <Heading as="h2" foregroundColor="primary" size="md" textAlign="start">
                        {booster.displayHeading}
                      </Heading>

                      <Paragraph foregroundColor="secondary" size="md" textAlign="start">
                        {booster.displayBodyText}
                      </Paragraph>
                    </Box>

                    <Paragraph foregroundColor="secondary" size="sm" textAlign="start">
                      {booster.displayFinePrint}
                    </Paragraph>
                  </Box>

                  <Box flexDirection="column" gap={{ base: "lg", lg: "sm" }}>
                    <Paragraph size="md" textAlign="start">
                      {booster.displaySectionLabel}
                    </Paragraph>

                    <Box flexDirection="column" gap="xs" role="radiogroup">
                      {booster.multiplierOptions.map((option) => (
                        <BoostMultiplierCard
                          key={option.id}
                          multiplier={option.percentage}
                          aviosAmount={option.aviosAmount}
                          price={option.price}
                          currency={booster.currency}
                          locale={booster.locale}
                          isSelected={booster.selectedBoostId === option.id}
                          showBestValue={!hasAnyDescription && option.isBestValue}
                          bestValueLabel={booster.displayBestValueLabel}
                          description={option.description}
                          onClick={() => booster.handleBoostSelect(option)}
                          disabled={booster.disabled}
                        />
                      ))}
                    </Box>

                    <Box justifyContent="end">
                      {booster.selectedBoostId && !booster.disabled ? (
                        <Button
                          styleVariant="accent"
                          emphasis="secondary"
                          size="md"
                          fullWidth={breakpoint !== "desktop"}
                          onPress={booster.handleRemoveBoost}
                        >
                          {booster.displayRemoveBoostLabel}
                        </Button>
                      ) : (
                        <Spacer h="3xl" />
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Box>

              <DetailsDisclosure>
                <DetailsDisclosure.Summary>
                  <Label size="md" foregroundColor={booster.disabled ? "disabledPrimary" : "accentPrimary"}>
                    <strong>{booster.displayTermsLabel}</strong>
                  </Label>
                </DetailsDisclosure.Summary>
                <DetailsDisclosure.Details>
                  {booster.displayTermsContent ? (
                    <Paragraph foregroundColor="secondary" size="md">
                      {booster.displayTermsContent}
                    </Paragraph>
                  ) : (
                    <Paragraph size="sm">Terms and conditions content will be displayed here.</Paragraph>
                  )}
                </DetailsDisclosure.Details>
              </DetailsDisclosure>
            </>
          )}
        </Box>
      </Section>
    );
  },
);

RewardPlatformBoosterUI.displayName = "RewardPlatformBoosterUI";
